import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreadcrumbService } from '../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../domain/services/dialog/dialog.service';
import { DistributionScheduleService } from '../../domain/services/distribution/distribution-schedule.service';
import { DistributionSchedule } from '../../domain/models/distribution-schedule.model';

/**
 * Distributions page (distribution design section 12 — Figma list, img 7/13).
 * One row per recurring SCHEDULE: its day-ranges, who created it, start/end months
 * and a derived status chip. Add / edit / delete a schedule; the generated runs and
 * their PDFs live under Reports ▸ Distribution runs.
 */
@Component({
  selector: 'app-distributions',
  standalone: true,
  imports: [
    CommonModule, RouterModule, MatCardModule, MatButtonModule, MatIconModule,
    MatTableModule, MatChipsModule, MatProgressBarModule, MatTooltipModule,
  ],
  templateUrl: './distributions.component.html',
  styleUrl: './distributions.component.css',
})
export class DistributionsComponent implements OnInit {
  schedules: DistributionSchedule[] = [];
  loading = false;
  error: string | null = null;

  readonly columns = ['ranges', 'createdBy', 'starts', 'ends', 'status', 'actions'];

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly scheduleService: DistributionScheduleService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/distributions', label: 'Administrative' },
      { icon: 'pie_chart', url: '/app/distributions', label: 'Distributions' },
    ]);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.scheduleService.list().subscribe({
      next: (list) => { this.schedules = list; this.loading = false; },
      error: () => { this.error = 'Failed to load distribution schedules.'; this.loading = false; },
    });
  }

  onAdd(): void {
    this.router.navigate(['/app/distributions/new']);
  }

  onEdit(s: DistributionSchedule): void {
    this.router.navigate(['/app/distributions', s.id]);
  }

  onDelete(s: DistributionSchedule): void {
    this.dialogService.openDeletedDialog(
      'Delete distribution schedule',
      `Are you sure you want to delete the schedule starting ${this.monthYear(s.startPeriod)}?`,
      (confirmed: boolean) => {
        if (confirmed) {
          this.scheduleService.delete(s.id).subscribe({
            next: () => this.load(),
            error: () => { this.error = 'Failed to delete schedule.'; },
          });
        }
      },
    );
  }

  viewRuns(): void {
    this.router.navigate(['/app/distributions/runs']);
  }

  /** yyyy-MM-01 -> MM/YYYY. */
  monthYear(period?: string | null): string {
    if (!period) { return 'N/A'; }
    const [y, m] = period.split('-');
    return `${m}/${y}`;
  }

  statusLabel(status: string): string {
    return status.charAt(0) + status.slice(1).toLowerCase();
  }
}
